// buildApsParamConformance.js — per-APS-field Checks detail rows.
//
// buildApsParamConformanceRows(results, dictionary, buSummary, sessionPlatform)
// turns a session's APS display check results into one row per request field:
// what the field should be (spec layer, always on, and BU layer, only when an
// App Pack matched), what this session actually sent, and how many ELIGIBLE
// requests FAIL it. Pure, ESM, never throws — see
// docs/aps-param-conformance-spec.md §3.5 for the column contract and
// .claude/skills/aps-param-conformance for the frozen Contract D.
//
// This is the GAM view's sibling (utils/buildParamConformance.js) with ONE
// column added: Platform. `pj.api`, `pj.att` and `skadn` exist only on iOS, so
// a platform-blind view renders them as failures on every Android session —
// which is precisely the bug §1.7 found in our own pack. `applicable:false`
// must render as "not applicable", never as a failure and never as a pass.
//
// ── The two counts that matter (spec §1.1) ─────────────────────────────────
// `matched` on a check result is "eligible requests". `failures.length` is
// "failing requests". `found` is a DIFFERENT metric and must never be read
// here; evidence arrays are capped at 50 rows and must never be counted.
//
// ── Wiring contract ────────────────────────────────────────────────────────
// `results` is an array of per-field check-result-shaped objects, each needing
// a `key` (the body dot-path it evaluates, e.g. check.assert.param) to be
// renderable. Beyond `key` an item may carry `matched`, `failures` (array,
// preferred) or `failureCount` (the slimmed wire shape), `status`, `severity`,
// and `expected`/`statement`/`pattern`/`values`. Merging suite definitions with
// run results is AgentPanel.jsx's job — that merge is wiring, not builder
// logic.
//
// `dictionary` is Contract E's ARRAY (served by GET /proxy/checks/aps-params),
// not a map — APS has no frontend dictionary file to import (spec §6.1). Its
// `meaning` prose is reused verbatim, never rewritten here.
//
// `buSummary` is Contract B's `byKey` map, or null when no App Pack matched.
// A wrapper `{ byKey }` is also accepted defensively. When null, every `bu*`
// field is null — the BU column renders "—", never a failure.
//
// `sessionPlatform` is what the session's traffic came from ('ios'|'android',
// or an IU-style id like 'iosmob'/'droidmob', or null when unknown). When it
// is null, every row is applicable: refusing to judge is better than hiding a
// field behind a platform guess.

const STATUS_RANK = { fail: 4, warn: 3, info: 2, pass: 1, not_evaluated: 0 };
const NOT_EVALUATED = 'not_evaluated';

/** evaluateCheck's status vocabulary, collapsed to the five verdicts this view renders. */
function normalizeStatus(status) {
  if (status === 'fail' || status === 'error') return 'fail';
  if (status === 'warn' || status === 'warning') return 'warn';
  if (status === 'info') return 'info';
  if (status === 'pass' || status === 'ok') return 'pass';
  return NOT_EVALUATED; // 'n/a', undefined, anything unrecognized
}

/** -1 = no data at all, 0..4 = the five renderable verdicts. */
function rankOf(verdict) {
  return Object.prototype.hasOwnProperty.call(STATUS_RANK, verdict) ? STATUS_RANK[verdict] : -1;
}

/** `failures.length`, never `found`. Falls back to the slimmed `failureCount`. */
function failingCountOf(result) {
  if (result && Array.isArray(result.failures)) return result.failures.length;
  if (result && typeof result.failureCount === 'number' && Number.isFinite(result.failureCount)) {
    return Math.max(0, Math.trunc(result.failureCount));
  }
  return 0;
}

/** `matched` — eligible requests. Never negative, never NaN. */
function eligibleCountOf(result) {
  const n = Number(result && result.matched);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0;
}

function expectedTextOf(result) {
  if (!result) return null;
  if (typeof result.expected === 'string' && result.expected) return result.expected;
  if (typeof result.statement === 'string' && result.statement) return result.statement;
  if (typeof result.pattern === 'string' && result.pattern) return `matches ${result.pattern}`;
  if (Array.isArray(result.values) && result.values.length) return `one of ${result.values.join(', ')}`;
  return null;
}

function keyOf(result) {
  return (result && typeof result.key === 'string' && result.key) ? result.key : null;
}

/** 'iosmob'/'ios'/'iPhone' -> 'ios'; 'droidmob'/'android' -> 'android'; else null.
 *  Compared by FAMILY because the APS payload carries no form-factor signal —
 *  an iOS-only field is iOS-only on an iPad too. */
function platformFamily(value) {
  const p = String(value || '').toLowerCase();
  if (!p) return null;
  if (p.startsWith('droid') || p.includes('android')) return 'android';
  if (p.startsWith('ios') || p.includes('iphone') || p.includes('ipad')) return 'ios';
  return null;
}

/** Whether a field scoped to `platforms` can be judged on this session. */
function isApplicable(platforms, sessionPlatform) {
  if (!Array.isArray(platforms) || !platforms.length) return true; // all platforms
  const session = platformFamily(sessionPlatform);
  if (!session) return true; // unknown session platform — judge rather than hide
  return platforms.some((p) => platformFamily(p) === session);
}

/** Among several results for one field, the one that ran and found the worst
 *  status wins; a result with 0 eligible rows never outranks one that ran. */
function worstOf(list) {
  let best = null;
  let bestRank = -1;
  for (const r of list) {
    const eligible = eligibleCountOf(r);
    const effectiveRank = eligible === 0 ? -1 : rankOf(normalizeStatus(r && r.status));
    if (effectiveRank > bestRank) { bestRank = effectiveRank; best = r; }
  }
  return best || list[0] || null;
}

const EMPTY_LAYER = { expected: null, severity: null, eligible: null, failing: null, verdict: null };

function buildSpecLayer(list) {
  if (!Array.isArray(list) || !list.length) return { ...EMPTY_LAYER };
  const chosen = worstOf(list);
  const eligible = eligibleCountOf(chosen);
  return {
    expected: expectedTextOf(chosen),
    severity: (chosen && typeof chosen.severity === 'string') ? chosen.severity : null,
    eligible,
    failing: failingCountOf(chosen),
    // eligible-0 is its own verdict: a check that never saw a request in scope
    // has not vouched for anything.
    verdict: eligible === 0 ? NOT_EVALUATED : normalizeStatus(chosen && chosen.status),
  };
}

function buildBuLayer(entry) {
  if (!entry || typeof entry !== 'object') return { ...EMPTY_LAYER };
  const eligible = eligibleCountOf({ matched: entry.eligible });
  const failing = failingCountOf({ failureCount: entry.failing });
  const severity = (typeof entry.severity === 'string' && entry.severity) ? entry.severity : null;

  let verdict;
  if (eligible === 0) verdict = NOT_EVALUATED;
  else if (failing > 0) verdict = (severity === 'fail' || severity === 'warn' || severity === 'info') ? severity : 'warn';
  else verdict = 'pass';

  return {
    expected: (typeof entry.expected === 'string' && entry.expected) ? entry.expected : null,
    severity,
    eligible,
    failing,
    verdict,
  };
}

/** Worst-of-two-layers, never averaged. The BU layer is capped at 'warn' here
 *  (skill §6 decision 3: a BU fail is our convention, not Amazon's, and must
 *  not block a launch); `buSeverity` still reports the authored severity. */
function pickVerdict(specLayer, buLayer) {
  const specRank = rankOf(specLayer.verdict);
  const buRank = Math.min(rankOf(buLayer.verdict), STATUS_RANK.warn);
  if (specRank < 0 && buRank < 0) return { verdict: null, verdictLayer: null };
  if (specRank >= buRank) return { verdict: specLayer.verdict, verdictLayer: 'spec' };
  const capped = rankOf(buLayer.verdict) > STATUS_RANK.warn ? 'warn' : buLayer.verdict;
  return { verdict: capped, verdictLayer: 'bu' };
}

function buildApsParamConformanceRows(results, dictionary, buSummary, sessionPlatform) {
  try {
    const resultList = Array.isArray(results) ? results.filter(Boolean) : [];
    const dictList = Array.isArray(dictionary) ? dictionary.filter((d) => d && d.key) : [];
    const dict = Object.fromEntries(dictList.map((d) => [d.key, d]));

    const buMap = (buSummary && typeof buSummary === 'object')
      ? (buSummary.byKey && typeof buSummary.byKey === 'object' ? buSummary.byKey : buSummary)
      : null;

    const specByKey = new Map();
    for (const r of resultList) {
      const key = keyOf(r);
      if (!key) continue;
      if (!specByKey.has(key)) specByKey.set(key, []);
      specByKey.get(key).push(r);
    }

    // Every documented field gets a row even when no check ran for it — a field
    // nobody checked is a gap worth seeing, not an absence worth hiding.
    const keys = new Set([
      ...dictList.map((d) => d.key),
      ...specByKey.keys(),
      ...(buMap ? Object.keys(buMap) : []),
    ]);

    const rows = [];
    for (const key of keys) {
      const dictEntry = dict[key] || null;
      const platforms = (dictEntry && Array.isArray(dictEntry.platforms) && dictEntry.platforms.length)
        ? dictEntry.platforms
        : null;
      const applicable = isApplicable(platforms, sessionPlatform);

      const specLayer = buildSpecLayer(specByKey.get(key));
      const buLayer = buMap ? buildBuLayer(buMap[key]) : { ...EMPTY_LAYER };
      const picked = pickVerdict(specLayer, buLayer);

      rows.push({
        key,
        label: (dictEntry && typeof dictEntry.label === 'string' && dictEntry.label) ? dictEntry.label : key,
        meaning: (dictEntry && typeof dictEntry.meaning === 'string' && dictEntry.meaning) ? dictEntry.meaning : null,
        platforms,
        applicable,
        specExpected: specLayer.expected || (dictEntry && dictEntry.expected) || null,
        specSeverity: specLayer.severity,
        specEligible: specLayer.eligible,
        specFailing: specLayer.failing,
        buExpected: buMap ? buLayer.expected : null,
        buSeverity: buMap ? buLayer.severity : null,
        buEligible: buMap ? buLayer.eligible : null,
        buFailing: buMap ? buLayer.failing : null,
        // A field this platform never sends has no verdict to give.
        verdict: applicable ? picked.verdict : null,
        verdictLayer: applicable ? picked.verdictLayer : null,
      });
    }

    // Worst first, so the rows worth looking at surface without scrolling past a
    // session's worth of quiet passes; not-applicable rows sink below every
    // judged row; alphabetical beneath so the order is stable across reruns.
    rows.sort((a, b) => {
      if (a.applicable !== b.applicable) return a.applicable ? -1 : 1;
      const byVerdict = rankOf(b.verdict) - rankOf(a.verdict);
      return byVerdict !== 0 ? byVerdict : a.key.localeCompare(b.key);
    });

    return rows;
  } catch {
    return [];
  }
}

export default buildApsParamConformanceRows;
export { buildApsParamConformanceRows };
